import { foldAddressText } from "./addressIdentity";

/**
 * The near-miss comparison behind the inline create form
 * (address-normalization/FR-012, /FR-013).
 *
 * It is deliberately NOT the identity fold. The fold decides when two spellings
 * are the SAME building and the server refuses the second outright (FR-003).
 * This decides when two spellings are close enough that a human should look
 * before a second building is registered — a warning, never a block, because the
 * two cases it must catch are indistinguishable from each other by string
 * distance alone: `3ης Σεπτεμ[β]ρίου 97` is a dropped letter (AS-013) while
 * `Αριστοτέλους 11` next to `Αριστοτέλους 111` is two real buildings (EC-011).
 * Any threshold catching the first necessarily catches the second, which is
 * exactly why the operator gets the last word.
 *
 * It runs entirely over the address list the page already holds (FR-013): no
 * endpoint, no `pg_trgm`, no request.
 */

/** The shape the comparison needs — satisfied by `responses.Address`. */
export interface NearMissCandidate {
    id: number;
    street: string;
    number: string;
    postalCode: string;
    canonical: string;
}

/** What the operator has typed into the inline create form. */
export interface AddressDraft {
    street: string;
    number: string;
    postalCode: string;
}

/**
 * The threshold, in edits over the FOLDED street, within one postal code.
 *
 * One. AS-013's typo is a single dropped `β`, so nothing larger would catch it,
 * and nothing smaller would catch anything at all. Widening it does not buy
 * safety — it buys warnings on unrelated streets that the operator learns to
 * click through, which is how a warning stops working.
 */
export const NEAR_MISS_MAX_STREET_DISTANCE = 1;

/**
 * levenshtein counts single-character insertions, deletions and substitutions
 * between two strings. Two rolling rows; the inputs here are street names, so
 * the quadratic cost is irrelevant.
 */
export function levenshtein(a: string, b: string): number {
    const left = Array.from(a);
    const right = Array.from(b);
    if (left.length === 0) {
        return right.length;
    }
    if (right.length === 0) {
        return left.length;
    }

    let previous = right.map((_, index) => index + 1);
    previous.unshift(0);

    for (let i = 0; i < left.length; i++) {
        const current = [i + 1];
        for (let j = 0; j < right.length; j++) {
            const substitution = previous[j] + (left[i] === right[j] ? 0 : 1);
            const deletion = previous[j + 1] + 1;
            const insertion = current[j] + 1;
            current.push(Math.min(substitution, deletion, insertion));
        }
        previous = current;
    }

    return previous[right.length];
}

/**
 * findAddressNearMisses returns the registered buildings the draft resembles,
 * closest first.
 *
 * The postal code is an exact (folded) gate rather than another distance: two
 * buildings in different postcodes are not the same building however alike their
 * streets read, and Athens reuses street names across postcodes freely. Within
 * one postcode the street is compared folded, so an accent or a Latin look-alike
 * is not mistaken for a real difference.
 *
 * A candidate at distance ZERO — the same street, a different number — is
 * included on purpose: that is EC-011's case and the commonest way a building is
 * entered twice. An exact identity match is included too; the server will refuse
 * it, but showing the operator the building they already have is a better answer
 * than a 400.
 */
export function findAddressNearMisses<T extends NearMissCandidate>(draft: AddressDraft, existing: readonly T[]): T[] {
    const postalCode = foldAddressText(draft.postalCode);
    const street = foldAddressText(draft.street);
    if (street === "" || postalCode === "") {
        return [];
    }

    return (
        existing
            .filter((candidate) => foldAddressText(candidate.postalCode) === postalCode)
            .map((candidate) => ({ candidate, distance: levenshtein(street, foldAddressText(candidate.street)) }))
            .filter(({ distance }) => distance <= NEAR_MISS_MAX_STREET_DISTANCE)
            // Closest first, then a stable, human-readable order so the same draft
            // always produces the same warning.
            .sort((a, b) => a.distance - b.distance || a.candidate.canonical.localeCompare(b.candidate.canonical) || a.candidate.id - b.candidate.id)
            .map(({ candidate }) => candidate)
    );
}
